import StackTracey from 'stacktracey';
import {
  ApiFaultOrWarnings,
  ApiFaultOrWarningsResponse
} from '../api/api.fault.interfaces';
import { isLocalDevelopmentEnvironment } from '../appSettings/app.environment';
import { clone } from '../utility/common';
import { ErrorConverter } from './ErrorConverter';
import {
  CAPTURED_ERROR_API_MESSAGE_PROPS,
  CAPTURED_ERROR_MESSAGE_PROPS,
  DEDUPLICATION_MAX_ENTRIES,
  DEDUPLICATION_TTL_MS,
  ERROR_LOG_COMPONENT_NAME_PREFIX,
  ERROR_LOG_ITEM_MAX_LENGTHS,
  ERROR_LOG_ITEM_MESSAGE_ATTRIBUTE_SEPARATOR,
  ERROR_LOG_ITEM_MESSAGE_SEPARATOR
} from './errorHandling.config';
import {
  CapturedError,
  CapturedErrorApiMessage,
  CapturedErrorBase,
  ComponentNameType,
  DEFAULT_ERROR_SEVERITY,
  EnhanceCapturedErrorParams,
  ERRORS_IGNORED_IN_DEV,
  ERROR_TYPE_NAMES,
  ErrorSubtypes,
  ErrorSubtypesSeverity,
  ErrorTypes,
  LogItems,
  LogMessageRequest
} from './errorHandling.types';
import { ErrorSanitizer } from './ErrorSanitizer';

/**
 * Handles error log formatting and submission to the LogMessage API,
 * with deduplication to prevent the double-submission bug.
 */
export class ErrorLogger {
  private recentHashes: Map<string, number> = new Map();

  private deduplicationTTL: number;

  constructor(deduplicationTTL: number = DEDUPLICATION_TTL_MS) {
    this.deduplicationTTL = deduplicationTTL;
  }

  /**
   * Full submission pipeline:
   * 1. Parse stack trace (if present)
   * 2. Enhance with application/user/browsing context
   * 3. Check dev-mode filtering
   * 4. Convert to LogMessage format
   * 5. Submit via the provided API function
   *
   * Catches all internal errors and resolves (never rejects) to prevent
   * infinite loops where a log submission failure triggers another error.
   */
  async submit(params: {
    capturedError: CapturedError;
    apiFunc: (payload: LogMessageRequest) => Promise<unknown>;
    applicationContext: EnhanceCapturedErrorParams['applicationContext'];
    userContext: EnhanceCapturedErrorParams['userContext'];
  }): Promise<void> {
    const { capturedError, apiFunc, applicationContext, userContext } = params;
    const transformedError = clone(capturedError);

    try {
      if (
        'stack' in transformedError.errorContext &&
        transformedError.errorContext.stack
      ) {
        const cleanedStack = await ErrorLogger.parseStackTrace(
          transformedError.errorContext.stack
        );
        transformedError.errorContext.stack = cleanedStack.asTable();
      }

      const enhancedError = ErrorLogger.enhanceCapturedError({
        applicationContext,
        capturedError: transformedError,
        userContext
      });

      if (ErrorLogger.isDevIgnoredError(enhancedError)) {
        return;
      }

      const logMessages = ErrorLogger.convertToLogMessages(enhancedError);
      const response = await apiFunc({ Messages: logMessages });

      if (
        !ErrorConverter.isSuccessResponseWithFault(
          response as ApiFaultOrWarningsResponse
        )
      ) {
        /* eslint-disable no-console */
        console.warn('Warning: Error submitted to logging service.');
        console.dir(enhancedError);
        /* eslint-enable no-console */
      }
    } catch {
      /* eslint-disable no-console */
      console.error('Error: Failed to submit error to logging service.');
      console.dir(transformedError);
      /* eslint-enable no-console */
    }
  }

  /**
   * Check whether an error is a duplicate of one recently submitted.
   * Uses a hash of (message + subtype + endpoint) with a TTL window.
   */
  isDuplicate(error: CapturedError): boolean {
    const hash = ErrorLogger.computeHash(error);
    const now = Date.now();
    const lastSeen = this.recentHashes.get(hash);

    if (lastSeen !== undefined && now - lastSeen < this.deduplicationTTL) {
      return true;
    }

    this.recentHashes.set(hash, now);
    this.cleanupOldEntries(now);

    return false;
  }

  /** Reset deduplication state (useful for testing). */
  resetDeduplication(): void {
    this.recentHashes.clear();
  }

  /**
   * Convert a captured error into the LogMessage API format.
   * Produces up to 4 LogItem entries: config, request, response, and contexts.
   */
  static convertToLogMessages(error: CapturedError): LogItems {
    const sanitizedError = ErrorSanitizer.sanitize(error);

    const componentName =
      sanitizedError.applicationContext.moduleName.substring(
        0,
        ERROR_LOG_ITEM_MAX_LENGTHS.ComponentName
      );

    let faultCode: null | number = null;
    const logItems: LogItems = [];
    const messagePrefix = `${sanitizedError.message}\n\n`;
    const messages: string[] = [];
    const messageParts: string[] = [
      `applicationContext: ${JSON.stringify({
        moduleName: sanitizedError.applicationContext.moduleName,
        moduleVersion: sanitizedError.applicationContext.moduleVersion
      })}`
    ];

    const originatingOperation =
      sanitizedError.browsingContext.url.substring(
        sanitizedError.browsingContext.url.length -
          ERROR_LOG_ITEM_MAX_LENGTHS.OriginatingOperation
      ) || null;

    if (ErrorConverter.isCapturedErrorApiError(sanitizedError)) {
      if (ErrorConverter.isCapturedApiErrorCoreFaultError(sanitizedError)) {
        faultCode = !sanitizedError.isBodyOrPayloadRedacted
          ? (
              sanitizedError.applicationContext.apiMessage.response
                ?.data as ApiFaultOrWarnings
            )?.Fault.Code || null
          : null;
      }

      CAPTURED_ERROR_API_MESSAGE_PROPS.forEach((prop) => {
        messages.push(
          `applicationContext.apiMessage.${prop}: ${JSON.stringify(
            (
              sanitizedError.applicationContext
                .apiMessage as CapturedErrorApiMessage
            )[prop] || {}
          )}`
        );
      });
    }

    CAPTURED_ERROR_MESSAGE_PROPS.forEach((context) => {
      messageParts.push(
        `${context}: ${JSON.stringify(
          sanitizedError[context as keyof CapturedErrorBase] || {}
        )}`
      );
    });

    messages.push(
      messageParts.join(ERROR_LOG_ITEM_MESSAGE_ATTRIBUTE_SEPARATOR)
    );

    return messages.reduce((result, message) => {
      result.push({
        Code: faultCode,
        ComponentName: componentName as ComponentNameType,
        Message: `${messagePrefix}${message.substring(
          0,
          ERROR_LOG_ITEM_MAX_LENGTHS.MessageWithoutSeparator -
            messagePrefix.length
        )}${ERROR_LOG_ITEM_MESSAGE_SEPARATOR}`,
        OriginatingOperation: originatingOperation,
        Severity: sanitizedError.severity || DEFAULT_ERROR_SEVERITY,
        SubComponentName: ''
      });
      return result;
    }, logItems);
  }

  /**
   * Enhance a captured error with application context, user context, browsing
   * context, severity, and error type name before submission.
   */
  static enhanceCapturedError<T>({
    applicationContext,
    capturedError,
    userContext
  }: EnhanceCapturedErrorParams): CapturedError<T> {
    const enhanced = clone(capturedError);

    let errorSeverity = capturedError.severity;

    if (Object.keys(capturedError.applicationContext.apiMessage).length) {
      const apiFaultDetails = capturedError.applicationContext
        .apiMessage as CapturedErrorApiMessage<ApiFaultOrWarnings>;

      if (
        ErrorConverter.isSuccessResponseWithFault(apiFaultDetails.response!)
      ) {
        errorSeverity = apiFaultDetails?.response?.data?.Fault.Severity;
      }
    } else {
      errorSeverity =
        ErrorSubtypesSeverity[enhanced.subtype] || DEFAULT_ERROR_SEVERITY;
    }

    errorSeverity = errorSeverity || DEFAULT_ERROR_SEVERITY;

    const moduleName =
      `${ERROR_LOG_COMPONENT_NAME_PREFIX}.${applicationContext.name}` as ComponentNameType;
    const pageUrl = new URL(document.URL);

    enhanced.applicationContext.moduleName = moduleName;
    enhanced.applicationContext.moduleVersion = applicationContext.version;
    enhanced.browsingContext = {
      url: `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`,
      userAgent: navigator.userAgent
    };
    enhanced.message = enhanced.message || enhanced.errorContext.message || '';
    enhanced.severity = errorSeverity;
    enhanced.userContext.displayLanguage = userContext.displayLanguage;
    enhanced.userContext.language = userContext.language;

    switch (enhanced.subtype) {
      case ErrorSubtypes.RUNTIME:
        enhanced.name = ERROR_TYPE_NAMES[ErrorSubtypes.RUNTIME];
        break;
      case ErrorSubtypes.UNHANDLED_PROMISE:
        enhanced.name = ERROR_TYPE_NAMES[ErrorSubtypes.UNHANDLED_PROMISE];
        break;
      default:
        enhanced.name = ERROR_TYPE_NAMES[ErrorTypes.APPLICATION];
        break;
    }

    return enhanced;
  }

  static parseStackTrace(errorStack: string): Promise<StackTracey> {
    const stack = new StackTracey(errorStack);
    return stack.cleanAsync().then((cleanStack) => Promise.resolve(cleanStack));
  }

  /**
   * Compute a deduplication hash from the error's key identifying properties:
   * message, subtype, and the API endpoint (if applicable).
   */
  static computeHash(error: CapturedError): string {
    const parts: string[] = [error.message || '', String(error.subtype)];

    const apiMessage = error.applicationContext?.apiMessage as
      | CapturedErrorApiMessage
      | undefined;
    if (apiMessage && 'config' in apiMessage) {
      parts.push(apiMessage.config?.url || '');
    }

    return parts.join('|');
  }

  private static isDevIgnoredError(error: CapturedError): boolean {
    if (!isLocalDevelopmentEnvironment()) return false;

    const errorMessage = error.errorContext?.message;
    if (!errorMessage) return false;

    return (
      errorMessage.startsWith(ERRORS_IGNORED_IN_DEV.MODULE_BUILD_FAILED) ||
      errorMessage.startsWith(ERRORS_IGNORED_IN_DEV.TOO_MANY_RERENDERS) ||
      errorMessage.endsWith(ERRORS_IGNORED_IN_DEV.NOT_A_FUNCTION) ||
      errorMessage.endsWith(ERRORS_IGNORED_IN_DEV.NOT_DEFINED) ||
      errorMessage === ERRORS_IGNORED_IN_DEV.LENGTH_OF_UNDEFINED
    );
  }

  private cleanupOldEntries(now: number): void {
    if (this.recentHashes.size <= DEDUPLICATION_MAX_ENTRIES) return;

    for (const [hash, timestamp] of this.recentHashes) {
      if (now - timestamp >= this.deduplicationTTL) {
        this.recentHashes.delete(hash);
      }
    }
  }
}
